import DateAndValue from "../DateAndValue";

export default class SDEAnalysis {
  constructor({
    independence = "AUTO",
    yearsFormatted = [],
    autoAssumptions = {},
    manualSalesRevenue = yearsFormatted.map((year) => ({ date: year, value: 0 })),
    manualNetIncome = yearsFormatted.map((year) => ({ date: year, value: 0 })),
    manualInterest = yearsFormatted.map((year) => ({ date: year, value: 0 })),
    manualTaxes = yearsFormatted.map((year) => ({ date: year, value: 0 })),
    manualDepreciation = yearsFormatted.map((year) => ({ date: year, value: 0 })),
    manualAmortization = yearsFormatted.map((year) => ({ date: year, value: 0 })),
    socialSecurityTax = 0,
    medicareTax = 0,
    matrixManagementAdj = [
      {
        id: 0,
        name: "",
        series: yearsFormatted.map((year) => ({ date: year, value: "" })),
        comment: "",
      },
    ],
    matrixDiligenceAdj = [
      {
        id: 0,
        name: "",
        series: yearsFormatted.map((year) => ({ date: year, value: "" })),
        comment: "",
      },
    ],
    matrixProFormaAdj = [
      {
        id: 0,
        name: "",
        series: yearsFormatted.map((year) => ({ date: year, value: "" })),
        comment: "",
      },
    ],
    matrixOwnerSalaryAdj = [
      {
        id: 0,
        name: "",
        series: yearsFormatted.map((year) => ({ date: year, value: "" })),
        comment: "",
      },
    ],
    noteManagement = "",
    noteDiligence = "",
    noteProForma = "",
    noteOwnerSalary = "",
  }) {
    this.independence = independence;
    this.yearsFormatted = yearsFormatted;
    this.autoAssumptions = autoAssumptions;
    this.manualSalesRevenue = manualSalesRevenue;
    this.manualNetIncome = manualNetIncome;
    this.manualInterest = manualInterest;
    this.manualTaxes = manualTaxes;
    this.manualDepreciation = manualDepreciation;
    this.manualAmortization = manualAmortization;
    this.socialSecurityTax = socialSecurityTax;
    this.medicareTax = medicareTax;
    this.matrixManagementAdj = matrixManagementAdj;
    this.matrixDiligenceAdj = matrixDiligenceAdj;
    this.matrixProFormaAdj = matrixProFormaAdj;
    this.matrixOwnerSalaryAdj = matrixOwnerSalaryAdj;
    this.noteManagement = noteManagement;
    this.noteDiligence = noteDiligence;
    this.noteProForma = noteProForma;
    this.noteOwnerSalary = noteOwnerSalary;
  }

  get EBITDA() {
    const { manualNetIncome, manualInterest, manualTaxes, manualDepreciation, manualAmortization } = this;

    if(this.independence === "AUTO") return this.autoAssumptions.EBITDA;
    else {
      return this.getArraySum(manualNetIncome, this.getArraySum(
        manualInterest, this.getArraySum(
          manualTaxes, this.getArraySum(manualDepreciation, manualAmortization)
        )
      ));
    }
  }

  get totalSalesRevenue() {
    if(this.independence === "AUTO") return this.autoAssumptions.salesRevenue;
    else return this.manualSalesRevenue;
  }

  get totalManagementAdj() {
    const { matrixManagementAdj } = this;
    return this.getMatrixSum(matrixManagementAdj);
  }

  get EBITDAManagementAdj() {
    const { EBITDA, totalManagementAdj } = this;
    return this.getArraySum(EBITDA, totalManagementAdj);
  }

  get EBITDAManagementMargin() {
    const { EBITDAManagementAdj } = this;
    return this.getMargin(EBITDAManagementAdj);
  }

  get totalDiligenceAdj() {
    const { matrixDiligenceAdj } = this;
    return this.getMatrixSum(matrixDiligenceAdj);
  }

  get EBITDADiligenceAdj() {
    const { EBITDAManagementAdj, totalDiligenceAdj } = this;
    return this.getArraySum(EBITDAManagementAdj, totalDiligenceAdj);
  }

  get EBITDADiligenceMargin() {
    const { EBITDADiligenceAdj } = this;
    return this.getMargin(EBITDADiligenceAdj);
  }

  get totalProFormaAdj() {
    const { matrixProFormaAdj } = this;
    return this.getMatrixSum(matrixProFormaAdj);
  }

  get EBITDAProFormaAdj() {
    const { EBITDADiligenceAdj, totalProFormaAdj } = this;
    return this.getArraySum(EBITDADiligenceAdj, totalProFormaAdj);
  }

  get EBITDAProFormaMargin() {
    const { EBITDAProFormaAdj } = this;
    return this.getMargin(EBITDAProFormaAdj);
  }

  get totalOwnerSalaryAdj() {
    const { matrixOwnerSalaryAdj } = this;
    return this.getMatrixSum(matrixOwnerSalaryAdj);
  }

  get totalSocialSecurityTaxes() {
    const { totalOwnerSalaryAdj, socialSecurityTax } = this;
    return this.getArrayMultiply(totalOwnerSalaryAdj, socialSecurityTax);
  }

  get totalMedicareTaxes() {
    const { totalOwnerSalaryAdj, medicareTax } = this;
    return this.getArrayMultiply(totalOwnerSalaryAdj, medicareTax);
  }

  get totalOwnerSalaryandPayrollTaxes() {
    const { totalOwnerSalaryAdj, socialSecurityTax, medicareTax } = this;
    return this.getArrayMultiply(totalOwnerSalaryAdj, 1 + socialSecurityTax + medicareTax);
  }

  get EBITDAOwnerSalaryAdj() {
    const { EBITDAProFormaAdj, totalOwnerSalaryandPayrollTaxes } = this;
    return this.getArraySum(EBITDAProFormaAdj, totalOwnerSalaryandPayrollTaxes);
  }

  get EBITDAOwnerSalaryMargin() {
    const { EBITDAOwnerSalaryAdj } = this;
    return this.getMargin(EBITDAOwnerSalaryAdj);
  }

  getMatrixSum(matrix) {
    //add each year's sum of item values(prices)
    return this.yearsFormatted.map(
      (year, index) =>
        new DateAndValue({
          date: year,
          value: matrix.reduce(
            (acc, item) => (acc += Number(item.series[index].value)),
            0
          ),
        })
    );
  }

  getArraySum(array1, array2) {
    //add element values of arrays
    return this.yearsFormatted.map(
      (year, index) =>
        new DateAndValue({
          date: year,
          value: array1[index].value + array2[index].value,
        })
    );
  }

  getArrayMultiply(array, num) {
    //multiply number to element values of arrays
    return this.yearsFormatted.map(
      (year, index) =>
        new DateAndValue({
          date: year,
          value: array[index].value * num,
        })
    );
  }

  getMargin(EBITDA) {
    //get margin array from specific EBITDA array
    return this.yearsFormatted.map(
      (year, index) =>
        new DateAndValue({
          date: year,
          value: (EBITDA[index].value / this.totalSalesRevenue[index].value) * 100,
        })
    );
  }
}
